/**
 * Passive browser-side Clerk session diagnostics (RUM `clerk_refresh_anomaly`, 401 header enrichment).
 * Wired from `src/pages/_app.tsx` (`SessionRecorder`, Datadog `beforeSend`).
 *
 * Active recovery when tRPC queries fail with UNAUTHORIZED while Clerk still shows a signed-in session
 * lives separately (SC-2589): `clerk-auth-recovery.service.ts` (coordinator + link),
 * `clerk-auth-recovery-session.registry.ts`, `useClerkAuthRecoverySessionRegistration`, first link in
 * `src/utils/api.ts`. Recovery emits `clerk_auth_recovery`; it does not use this module's sampler.
 *
 * Same-origin `/api/trpc` responses that include `x-shelfcycle-trpc-unauthorized: 1` (HTTP 207 batches included)
 * are annotated on the RUM resource event as `trpc_unauthorized: true` — one signal per batch request.
 */
import { isTrpcUnauthorizedBatchResponseHeader, TRPC_UNAUTHORIZED_BATCH_HEADER } from "~/shared/trpc/trpc-unauthorized-batch.constants";

const CLERK_AUTH_REASON_HEADER = "x-clerk-auth-reason";
const CLERK_AUTH_STATUS_HEADER = "x-clerk-auth-status";
const CLERK_SESSION_COOKIE = /(?:^|;\s*)__session(?:_[^=;]+)?=([^;]*)/;
const SAMPLE_INTERVAL_MS = 5_000;
/**
 * Clerk issues session tokens with a 60 second lifetime and refreshes them ahead of expiry, so a
 * token still in place after two lifetimes means the passive refresh is not landing in this tab.
 */
const STALE_TOKEN_AFTER_MS = 120_000;
const MAX_TOKEN_PAYLOAD_LENGTH = 4_096;

export const CLERK_REFRESH_ANOMALY_ACTION = "clerk_refresh_anomaly";

export type LockHeartbeat = "fresh" | "stale" | "absent" | "unknown";
export type LastTokenOutcome = "none" | "success" | "http_error" | "network_error";
export type AnomalyKind = "stale_token";
export type DurationBucket = "15_30s" | "30_60s" | "60_90s" | "90_180s" | "180s_plus";

export type ClerkRefreshAnomaly = {
  kind: AnomalyKind;
  lock_heartbeat: LockHeartbeat;
  last_token_outcome: LastTokenOutcome;
  duration_bucket: DurationBucket;
  diagnostic_version: 2;
};

export type ClerkAuthStatus = "signed-in" | "signed-out" | "interstitial" | "handshake" | "unknown";
export type ClerkAuthReason =
  | "cookie-and-uat-missing"
  | "cookie-missing"
  | "cookie-outdated"
  | "uat-missing"
  | "cross-origin-referrer"
  | "header-missing-cors"
  | "header-missing-non-browser"
  | "satellite-needs-syncing"
  | "satellite-returns-from-primary"
  | "primary-responds-to-syncing"
  | "standard-signed-in"
  | "standard-signed-out"
  | "unexpected-error"
  | "token-expired"
  | "token-invalid"
  | "token-invalid-algorithm"
  | "token-invalid-authorized-parties"
  | "token-invalid-issuer"
  | "token-invalid-signature"
  | "token-not-active-yet"
  | "token-verification-failed"
  | "secret-key-invalid"
  | "jwk-local-missing"
  | "jwk-remote-failed-to-load"
  | "jwk-remote-invalid"
  | "jwk-remote-missing"
  | "jwk-failed-to-resolve"
  | "interstitial-remote-failed-to-load"
  // Added by the Clerk Backend v2 line; the v4 names above are kept so historical events still map.
  | "session-token-and-uat-missing"
  | "session-token-missing"
  | "session-token-expired"
  | "session-token-nbf"
  | "invalid-session-token"
  | "expired-session-token-decode-failed"
  | "missing-session-token"
  | "missing-refresh-token"
  | "dev-browser-missing"
  | "dev-browser-sync"
  | "active-organization-mismatch"
  | "primary-domain-cross-origin-sync"
  | "non-eligible-no-refresh-cookie"
  | "non-eligible-non-get"
  | "jwk-kid-mismatch"
  | "token-iat-in-the-future"
  | "fetch-error"
  | "missing-api-client"
  | "signed-in"
  | "signed-out"
  // Remaining `AuthErrorReason` values that reach the header (@clerk/backend dist/index.js:5322-5339).
  | "client-uat-but-no-session-token"
  | "session-token-but-no-client-uat"
  | "session-token-iat-before-client-uat"
  | "session-token-iat-in-the-future"
  | "token-type-mismatch"
  | "protect-rewrite"
  // On the handshake path the header carries a COMPOSED value, not a bare reason:
  // `${AuthErrorReason.SessionTokenExpired}-refresh-${RefreshTokenErrorReason}`
  // (@clerk/backend dist/index.js:6372, the only composition site). Without these keys the most
  // common 401 — an expired session token on a non-GET request — mapped to "unknown".
  | "session-token-expired-refresh-non-eligible-no-refresh-cookie"
  | "session-token-expired-refresh-non-eligible-non-get"
  | "session-token-expired-refresh-invalid-session-token"
  | "session-token-expired-refresh-missing-api-client"
  | "session-token-expired-refresh-missing-session-token"
  | "session-token-expired-refresh-missing-refresh-token"
  | "session-token-expired-refresh-expired-session-token-decode-failed"
  | "session-token-expired-refresh-expired-session-token-missing-sid-claim"
  | "session-token-expired-refresh-fetch-error"
  | "session-token-expired-refresh-unexpected-sdk-error"
  | "session-token-expired-refresh-unexpected-bapi-error"
  | "unknown";

type TimerHandle = number;

export type ClerkRefreshDiagnosticsEnvironment = {
  now: () => number;
  setInterval: (callback: () => void, delayMs: number) => TimerHandle | null;
  clearInterval: (handle: TimerHandle) => void;
  getVisibilityState: () => "visible" | "hidden" | null;
  isOnline: () => boolean | null;
  getOrigin: () => string | null;
  /** Required, not optional: an absent reader would silence the only probe with a green build. */
  getSessionToken: () => string | null;
};

type RuntimeState = {
  issuedAtMs: number | null;
  eligibleMs: number;
  lastSampleAt: number | null;
  episodeEmitted: boolean;
};

type HeaderReader = {
  get: (name: string) => string | null;
};

type XhrHeaderReader = {
  getResponseHeader: (name: string) => string | null;
};

const AUTH_STATUS_VALUES: Record<string, ClerkAuthStatus> = {
  "signed-in": "signed-in",
  "signed-out": "signed-out",
  interstitial: "interstitial",
  handshake: "handshake",
  unknown: "unknown",
};

const AUTH_REASON_VALUES: Record<string, ClerkAuthReason> = {
  "cookie-and-uat-missing": "cookie-and-uat-missing",
  "cookie-missing": "cookie-missing",
  "cookie-outdated": "cookie-outdated",
  "uat-missing": "uat-missing",
  "cross-origin-referrer": "cross-origin-referrer",
  "header-missing-cors": "header-missing-cors",
  "header-missing-non-browser": "header-missing-non-browser",
  "satellite-needs-syncing": "satellite-needs-syncing",
  "satellite-returns-from-primary": "satellite-returns-from-primary",
  "primary-responds-to-syncing": "primary-responds-to-syncing",
  "standard-signed-in": "standard-signed-in",
  "standard-signed-out": "standard-signed-out",
  "unexpected-error": "unexpected-error",
  "token-expired": "token-expired",
  "token-invalid": "token-invalid",
  "token-invalid-algorithm": "token-invalid-algorithm",
  "token-invalid-authorized-parties": "token-invalid-authorized-parties",
  "token-invalid-issuer": "token-invalid-issuer",
  "token-invalid-signature": "token-invalid-signature",
  "token-not-active-yet": "token-not-active-yet",
  "token-verification-failed": "token-verification-failed",
  "secret-key-invalid": "secret-key-invalid",
  "jwk-local-missing": "jwk-local-missing",
  "jwk-remote-failed-to-load": "jwk-remote-failed-to-load",
  "jwk-remote-invalid": "jwk-remote-invalid",
  "jwk-remote-missing": "jwk-remote-missing",
  "jwk-failed-to-resolve": "jwk-failed-to-resolve",
  "interstitial-remote-failed-to-load": "interstitial-remote-failed-to-load",
  "session-token-and-uat-missing": "session-token-and-uat-missing",
  "session-token-missing": "session-token-missing",
  "session-token-expired": "session-token-expired",
  "session-token-nbf": "session-token-nbf",
  "invalid-session-token": "invalid-session-token",
  "expired-session-token-decode-failed": "expired-session-token-decode-failed",
  "missing-session-token": "missing-session-token",
  "missing-refresh-token": "missing-refresh-token",
  "dev-browser-missing": "dev-browser-missing",
  "dev-browser-sync": "dev-browser-sync",
  "active-organization-mismatch": "active-organization-mismatch",
  "primary-domain-cross-origin-sync": "primary-domain-cross-origin-sync",
  "non-eligible-no-refresh-cookie": "non-eligible-no-refresh-cookie",
  "non-eligible-non-get": "non-eligible-non-get",
  "jwk-kid-mismatch": "jwk-kid-mismatch",
  "token-iat-in-the-future": "token-iat-in-the-future",
  "fetch-error": "fetch-error",
  "missing-api-client": "missing-api-client",
  "signed-in": "signed-in",
  "signed-out": "signed-out",
  "client-uat-but-no-session-token": "client-uat-but-no-session-token",
  "session-token-but-no-client-uat": "session-token-but-no-client-uat",
  "session-token-iat-before-client-uat": "session-token-iat-before-client-uat",
  "session-token-iat-in-the-future": "session-token-iat-in-the-future",
  "token-type-mismatch": "token-type-mismatch",
  "protect-rewrite": "protect-rewrite",
  "session-token-expired-refresh-non-eligible-no-refresh-cookie": "session-token-expired-refresh-non-eligible-no-refresh-cookie",
  "session-token-expired-refresh-non-eligible-non-get": "session-token-expired-refresh-non-eligible-non-get",
  "session-token-expired-refresh-invalid-session-token": "session-token-expired-refresh-invalid-session-token",
  "session-token-expired-refresh-missing-api-client": "session-token-expired-refresh-missing-api-client",
  "session-token-expired-refresh-missing-session-token": "session-token-expired-refresh-missing-session-token",
  "session-token-expired-refresh-missing-refresh-token": "session-token-expired-refresh-missing-refresh-token",
  "session-token-expired-refresh-expired-session-token-decode-failed": "session-token-expired-refresh-expired-session-token-decode-failed",
  "session-token-expired-refresh-expired-session-token-missing-sid-claim":
    "session-token-expired-refresh-expired-session-token-missing-sid-claim",
  "session-token-expired-refresh-fetch-error": "session-token-expired-refresh-fetch-error",
  "session-token-expired-refresh-unexpected-sdk-error": "session-token-expired-refresh-unexpected-sdk-error",
  "session-token-expired-refresh-unexpected-bapi-error": "session-token-expired-refresh-unexpected-bapi-error",
  unknown: "unknown",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function emptyRuntimeState(): RuntimeState {
  return {
    issuedAtMs: null,
    eligibleMs: 0,
    lastSampleAt: null,
    episodeEmitted: false,
  };
}

/**
 * Reads the `iat` claim of a Clerk session token and returns it in milliseconds. The token is
 * never emitted; only the bucketed age derived from it leaves this module. Malformed input,
 * an unreadable payload, and a token issued in the future all fail closed to `null`.
 */
export function parseSessionTokenIssuedAt(raw: string, now: number): number | null {
  if (!isFiniteNumber(now)) {
    return null;
  }

  const parts = raw.split(".");
  const payload = parts.length === 3 ? parts[1] : undefined;
  if (payload === undefined || payload.length === 0 || payload.length > MAX_TOKEN_PAYLOAD_LENGTH) {
    return null;
  }

  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const claims: unknown = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")));
    if (!isRecord(claims) || !isFiniteNumber(claims.iat)) {
      return null;
    }

    const issuedAtMs = claims.iat * 1_000;
    return issuedAtMs > now ? null : issuedAtMs;
  } catch {
    return null;
  }
}

/**
 * Extracts Clerk's session token from a `document.cookie` string. Clerk names the cookie
 * `__session`, suffixed per instance when several share a domain. Exported so the exact cookie
 * name this probe depends on is covered by a test rather than only by production.
 */
export function parseSessionCookie(cookie: string): string | null {
  const value = CLERK_SESSION_COOKIE.exec(cookie)?.[1];
  if (value === undefined || value.length === 0) {
    return null;
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

function readSessionToken(environment: ClerkRefreshDiagnosticsEnvironment): string | null {
  try {
    return environment.getSessionToken();
  } catch {
    return null;
  }
}

function isHeaderReader(value: unknown): value is HeaderReader {
  return isRecord(value) && typeof value.get === "function";
}

function isXhrHeaderReader(value: unknown): value is XhrHeaderReader {
  return isRecord(value) && typeof value.getResponseHeader === "function";
}

function readResponseHeader(context: unknown, name: string): string | null {
  if (!isRecord(context)) {
    return null;
  }

  const response = context.response;
  if (isRecord(response) && isHeaderReader(response.headers)) {
    try {
      return response.headers.get(name);
    } catch {
      return null;
    }
  }

  if (isXhrHeaderReader(context.xhr)) {
    try {
      return context.xhr.getResponseHeader(name);
    } catch {
      return null;
    }
  }

  return null;
}

function resourceFromEvent(event: unknown): Record<string, unknown> | null {
  if (!isRecord(event) || event.type !== "resource" || !isRecord(event.resource)) {
    return null;
  }
  return event.resource;
}

function mergeEventContext(event: unknown, patch: Record<string, unknown>): void {
  if (!isRecord(event)) {
    return;
  }

  event.context = {
    ...(isRecord(event.context) ? event.context : {}),
    ...patch,
  };
}

function addAuthContext(event: unknown, reason: ClerkAuthReason, status: ClerkAuthStatus): void {
  mergeEventContext(event, {
    clerk_auth_reason: reason,
    clerk_auth_status: status,
  });
}

function addTrpcUnauthorizedContext(event: unknown): void {
  mergeEventContext(event, { trpc_unauthorized: true });
}

function isTrpcApiResource(url: unknown, origin: string | null): boolean {
  if (typeof url !== "string") {
    return false;
  }

  try {
    const parsed = origin === null ? new URL(url) : new URL(url, origin);
    return parsed.pathname === "/api/trpc" || parsed.pathname.startsWith("/api/trpc/");
  } catch {
    return false;
  }
}

function isSameOriginResource(url: unknown, origin: string | null): boolean | null {
  if (typeof url !== "string") {
    return null;
  }

  try {
    const parsed = origin === null ? new URL(url) : new URL(url, origin);
    return origin !== null && parsed.origin === origin;
  } catch {
    return null;
  }
}

function isFetchResource(resource: Record<string, unknown>): boolean {
  return resource.type === "fetch" || resource.type === "xhr";
}

export function mapClerkAuthStatus(value: string | null): ClerkAuthStatus {
  if (value === null || !Object.prototype.hasOwnProperty.call(AUTH_STATUS_VALUES, value)) {
    return "unknown";
  }
  const mappedValue = AUTH_STATUS_VALUES[value];
  return mappedValue ?? "unknown";
}

export function mapClerkAuthReason(value: string | null): ClerkAuthReason {
  if (value === null || !Object.prototype.hasOwnProperty.call(AUTH_REASON_VALUES, value)) {
    return "unknown";
  }
  const mappedValue = AUTH_REASON_VALUES[value];
  return mappedValue ?? "unknown";
}

function durationBucket(durationMs: number): DurationBucket {
  if (durationMs < 30_000) {
    return "15_30s";
  }
  if (durationMs < 60_000) {
    return "30_60s";
  }
  if (durationMs < 90_000) {
    return "60_90s";
  }
  if (durationMs < 180_000) {
    return "90_180s";
  }
  return "180s_plus";
}

/** Exported so a test can pin the production wiring, not just the parsers it delegates to. */
export function defaultEnvironment(): ClerkRefreshDiagnosticsEnvironment {
  return {
    now: () => Date.now(),
    setInterval: (callback, delayMs) => {
      if (typeof window === "undefined") {
        return null;
      }
      return window.setInterval(callback, delayMs);
    },
    clearInterval: handle => {
      if (typeof window !== "undefined") {
        window.clearInterval(handle);
      }
    },
    getVisibilityState: () => (typeof document === "undefined" ? null : document.visibilityState),
    isOnline: () => (typeof navigator === "undefined" ? null : navigator.onLine),
    getOrigin: () => (typeof window === "undefined" ? null : window.location.origin),
    getSessionToken: () => {
      if (typeof document === "undefined") {
        return null;
      }
      try {
        return parseSessionCookie(document.cookie);
      } catch {
        return null;
      }
    },
  };
}

export function createClerkRefreshDiagnosticsService(
  environment: ClerkRefreshDiagnosticsEnvironment = defaultEnvironment(),
  emit: (action: ClerkRefreshAnomaly) => void = () => undefined,
) {
  let timerHandle: TimerHandle | null = null;
  let state = emptyRuntimeState();

  const reset = () => {
    state = emptyRuntimeState();
  };

  const safeEmit = (action: ClerkRefreshAnomaly) => {
    try {
      emit(action);
    } catch {
      // Diagnostic telemetry must never affect the application lifecycle.
    }
  };

  const emitAnomaly = (action: ClerkRefreshAnomaly) => {
    state.episodeEmitted = true;
    safeEmit(action);
  };

  const sample = (isAppSessionEligible: () => boolean) => {
    let now: number;
    try {
      now = environment.now();
    } catch {
      state.lastSampleAt = null;
      return;
    }

    if (!isFiniteNumber(now)) {
      state.lastSampleAt = null;
      return;
    }

    let eligible = false;
    try {
      eligible = isAppSessionEligible() && environment.getVisibilityState() === "visible" && environment.isOnline() === true;
    } catch {
      eligible = false;
    }

    if (!eligible) {
      state.lastSampleAt = null;
      return;
    }

    // Credit at most one skipped tick per sample. Timers do not fire while a machine sleeps, so an
    // uncapped delta would let a single 30-minute gap satisfy the dwell gate below with no time
    // actually observed — which is the opposite of what that gate is for.
    const elapsedMs = state.lastSampleAt === null ? 0 : Math.min(Math.max(0, now - state.lastSampleAt), SAMPLE_INTERVAL_MS * 2);
    state.lastSampleAt = now;

    const token = readSessionToken(environment);
    const issuedAtMs = token === null ? null : parseSessionTokenIssuedAt(token, now);
    if (issuedAtMs === null) {
      // Leave the tracked token in place: nulling it here would re-arm the episode and let the
      // same token emit twice once it becomes readable again.
      return;
    }

    if (state.issuedAtMs !== issuedAtMs) {
      state.issuedAtMs = issuedAtMs;
      state.eligibleMs = 0;
      state.episodeEmitted = false;
    } else {
      state.eligibleMs += elapsedMs;
    }

    // Two independent clocks must agree before this alarms. `ageMs` compares a server-issued `iat`
    // against the client wall clock, so a skewed client inflates it; `eligibleMs` only ever counts
    // observed time between eligible samples, so skew cannot manufacture it.
    const ageMs = now - issuedAtMs;
    if (!state.episodeEmitted && ageMs > STALE_TOKEN_AFTER_MS && state.eligibleMs > STALE_TOKEN_AFTER_MS) {
      emitAnomaly({
        kind: "stale_token",
        lock_heartbeat: "unknown",
        last_token_outcome: "none",
        duration_bucket: durationBucket(ageMs),
        diagnostic_version: 2,
      });
    }
  };

  const stop = () => {
    if (timerHandle !== null) {
      try {
        environment.clearInterval(timerHandle);
      } catch {
        // Cleanup is best effort and must remain passive.
      }
      timerHandle = null;
    }
    reset();
  };

  const start = (isAppSessionEligible: () => boolean): (() => void) => {
    stop();
    try {
      timerHandle = environment.setInterval(() => sample(isAppSessionEligible), SAMPLE_INTERVAL_MS);
    } catch {
      timerHandle = null;
    }
    return stop;
  };

  const observeRumEvent = (event: unknown, context: unknown): void => {
    try {
      const resource = resourceFromEvent(event);
      if (resource === null || !isFetchResource(resource)) {
        return;
      }

      const sameOrigin = isSameOriginResource(resource.url, environment.getOrigin());
      if (sameOrigin === null) {
        return;
      }

      if (resource.status_code === 401 && sameOrigin) {
        addAuthContext(
          event,
          mapClerkAuthReason(readResponseHeader(context, CLERK_AUTH_REASON_HEADER)),
          mapClerkAuthStatus(readResponseHeader(context, CLERK_AUTH_STATUS_HEADER)),
        );
      }

      if (
        sameOrigin &&
        isTrpcApiResource(resource.url, environment.getOrigin()) &&
        isTrpcUnauthorizedBatchResponseHeader(readResponseHeader(context, TRPC_UNAUTHORIZED_BATCH_HEADER))
      ) {
        addTrpcUnauthorizedContext(event);
      }
    } catch {
      // Unexpected RUM shapes and browser API failures are intentionally ignored.
    }
  };

  return { start, stop, observeRumEvent };
}
